import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { useSettings } from "../lib/SettingsContext";
import { api } from "../lib/api";
import { Quote, Building2, Users, Target, Compass } from "lucide-react";

export default function About() {
  const { settings } = useSettings();
  const [teachers, setTeachers] = useState([]);
  const [teacherCount, setTeacherCount] = useState(0);
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    api.get("/teachers?limit=200").then(({ data }) => {
      const order = ["Kepala Sekolah", "Wakasek Kurikulum", "Wakasek Kesiswaan", "Wakasek Humas", "Wakasek Sarpras", "Kepala Tata Usaha", "Guru", "Guru BK"];
      const sorted = [...data.items].sort((a, b) => {
        const ia = order.indexOf(a.position); const ib = order.indexOf(b.position);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
      setTeachers(sorted.slice(0, 8));
      setTeacherCount(data.total);
    }).catch(() => {});
    api.get("/facilities?limit=6").then(({ data }) => setFacilities(data.items)).catch(() => {});
  }, []);

  const s = settings || {};

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="Tentang Kami"
        title="Mengenal SMAS YPK 2 BIAK"
        subtitle="Sejak 1991, SMAS YPK 2 Biak hadir di Jl. Yos Sudarso No. 31 — berdiri teguh sebagai sekolah Kristen yang membentuk generasi berakhlak, unggul, responsif, dan mandiri."
        image={s.hero_image_url || s.building_image_url || "https://images.unsplash.com/photo-1577896851231-70ef18881754?w=2000&q=80"}
      />

      {/* IDENTITAS */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            {s.logo_url && (
              <div className="mb-6 inline-flex p-4 rounded-2xl bg-slate-50 border border-slate-200">
                <img src={s.logo_url} alt="Logo SMAS YPK 2 BIAK" className="w-28 h-28 object-contain" />
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Identitas Sekolah</div>
            <h2 className="font-serif text-3xl font-bold text-[#0B1D3A]">Profil Resmi</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              SMAS YPK 2 Biak adalah sekolah menengah atas swasta di bawah naungan Yayasan Pendidikan Kristen (YPK), berlokasi di Biak Numfor, Papua. Akreditasi A dari BAN-S/M.
            </p>
          </div>
          <div className="lg:col-span-2 card-elevated p-8">
            <dl className="grid sm:grid-cols-2 gap-y-5 gap-x-8 text-sm">
              {[
                ["Nama Sekolah", "SMAS YPK 2 BIAK"],
                ["NPSN", "60301717"],
                ["Jenjang", "SMA Swasta"],
                ["Alamat", "JL. Yos Sudarso No. 31"],
                ["Kelurahan", "Mandala, Biak Kota"],
                ["Kabupaten", "Biak Numfor, Papua"],
                ["Berdiri", "3 Oktober 1991"],
                ["SK Pendirian", "1338/I18.g/R.6/91"],
                ["Akreditasi", "A (No. 027/BAN-PDM/SK/2025)"],
                ["Status", "Yayasan Pendidikan Kristen"],
                ["Email", "ypkbiaksma@gmail.com"],
                ["Website", "smaypk2biak.sch.id"],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-slate-100 pb-2">
                  <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">{k}</dt>
                  <dd className="font-semibold text-[#0B1D3A]">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* VISI MISI */}
      <section data-testid="vision-mission" className="py-16 lg:py-24 bg-slate-50 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-[#0B1D3A] to-amber-500" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Visi & Misi</div>
            <h2 className="font-serif text-3xl sm:text-5xl font-bold text-[#0B1D3A]">Komitmen Pendidikan Kami</h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 card-elevated p-10 bg-[#0B1D3A] text-white relative overflow-hidden">
              <div className="absolute inset-0 papua-pattern-dark opacity-50" />
              <div className="relative">
                <Target className="w-10 h-10 text-amber-400" />
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-400 mt-6">Visi</div>
                <h3 className="font-serif text-3xl sm:text-4xl font-black mt-3 leading-tight">{s.vision || "GARAM"}</h3>
                <p className="mt-6 text-sm text-slate-300 leading-relaxed">
                  Terinspirasi dari metafora "menjadi garam dunia", kami membentuk generasi yang memberi rasa & dampak di masyarakat.
                </p>
              </div>
            </div>
            <div className="lg:col-span-3 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <Compass className="w-6 h-6 text-amber-600" />
                <h3 className="font-serif text-2xl font-bold text-[#0B1D3A]">Misi Kami</h3>
              </div>
              {(s.mission || []).map((m, i) => (
                <div key={i} className="card-elevated p-5 flex gap-4">
                  <div className="font-serif text-3xl font-black text-amber-500 leading-none">{i + 1}</div>
                  <p className="text-slate-700 leading-relaxed">{m}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPAL */}
      <section data-testid="principal-full" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img src={s.principal_photo_url} alt="Kepala Sekolah" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Sambutan Kepala Sekolah</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A] leading-tight">
              Pesan dari {s.principal_name || "Ibu Marice Sroyer, S.Pd"}
            </h2>
            <Quote className="w-10 h-10 text-amber-500/40 mt-6" />
            <p className="mt-3 text-lg text-slate-700 leading-relaxed italic">{s.principal_message}</p>
            <div className="mt-8 flex items-center gap-5">
              <div className="h-[1px] w-16 bg-amber-500" />
              <div>
                <div className="font-serif text-lg font-semibold text-[#0B1D3A]">{s.principal_name}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Kepala SMAS YPK 2 Biak</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEACHERS */}
      <section data-testid="teachers-preview" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Tenaga Pendidik</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Guru & Tenaga Kependidikan</h2>
              <p className="mt-3 text-slate-600 max-w-2xl">Didukung {teacherCount || 34} pendidik dan tenaga kependidikan — kepala sekolah, wakasek, guru mata pelajaran, guru BK, dan staf — dengan dedikasi tinggi.</p>
            </div>
            <Link to="/guru" className="text-sm font-semibold text-[#0B1D3A] hover:text-amber-600 link-underline inline-flex items-center gap-2">
              Lihat Semua →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {teachers.map((t) => (
              <div key={t.id} data-testid={`teacher-card-${t.id}`} className="card-elevated p-5 text-center">
                <div className="aspect-square w-full rounded-xl overflow-hidden mb-4 bg-slate-100">
                  <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div className="font-serif font-bold text-[#0B1D3A] leading-tight">{t.name}{t.title ? `, ${t.title}` : ""}</div>
                <div className="text-xs text-slate-500 mt-1">{t.position}</div>
                <div className="text-xs text-amber-600 mt-1 font-semibold">{t.subject}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Fasilitas</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Fasilitas Pembelajaran</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {facilities.map((f) => (
              <div key={f.id} className="card-elevated overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img src={f.image_url} alt={f.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <Building2 className="w-6 h-6 text-amber-600" />
                  <div className="font-serif font-bold text-lg text-[#0B1D3A] mt-3">{f.name}</div>
                  <p className="mt-2 text-sm text-slate-600">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
